import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { cloneDeep, round } from 'lodash-es';
import { BehaviorSubject, Subscription } from 'rxjs';
import { Constants } from '../../../common/constants';
import { AndelCombochartBaseComponent } from '../../../abin-resultat-rapport/base-components';
import { AbinResultDto } from '../../../inge-api/abinclient/models';
import { GoogleCombochartComponent } from '../../../google-charts/google-charts-base-components/google-combochart/google-combochart.component';

@Component({
    selector: 'app-rase-konkurrens-chart',
    templateUrl: './rase-konkurrens-chart.component.html',
    styleUrls: ['./rase-konkurrens-chart.component.scss']
})
export class RaseKonkurrensChartComponent extends AndelCombochartBaseComponent implements OnInit, OnDestroy {
    @ViewChild('chartContainer') chartContainer: ElementRef;

    @ViewChild(GoogleCombochartComponent, { static: true })
    googleCombochart: GoogleCombochartComponent;

    private subs: Subscription[];

    private _diagramBeskrivning = new BehaviorSubject<string>(null);
    diagramBeskrivning$ = this._diagramBeskrivning.asObservable();

    data: any[][];

    @Input()
    set diagramBeskrivning(val: string) {
        if (val) this._diagramBeskrivning.next(val);
    }

    @Input()
    set inventeringar(val: AbinResultDto[]) {
        if (val) {
            const sorted = cloneDeep(val).sort((a, b) => a.invAr - b.invAr);
            this._inventeringar.next(sorted);
        }
    }

    public chartName: string = Constants.DIAGRAM_NAMN.KONKURRENS_RASE;

    ngOnInit(): void {
        this.subs = [];
        this.subs.push(
            this.inventeringar$.subscribe((x) => {
                if (x && x.length > 0) {
                    const data: any[][] = [
                        [
                            'År',
                            { id: 'Arsskada_1ar', type: 'number', label: 'Årsvärde' },
                            { id: 'Medelfel_low', type: 'number', role: 'interval' },
                            { id: 'Medelfel_high', type: 'number', role: 'interval' },
                            { id: 'tool_tip_1ar', type: 'string', role: 'tooltip', p: { html: true } },
                            { id: 'Medel_3ar', type: 'number', label: 'Sammanvägning för de tre senaste inventeringarna' },
                            { id: 'Medelfel_3ar_low', type: 'number', role: 'interval' },
                            { id: 'Medelfel_3ar_high', type: 'number', role: 'interval' },
                            { id: 'tool_tip_3ar', type: 'string', role: 'tooltip', p: { html: true } }
                        ]
                    ];

                    x.forEach((inv) => {
                        if (inv.antalRASEGynnsamHa || inv.antalRASEGynnsamHa === 0) {
                            // Årsskada
                            const arsskadaAndel = inv.antalRASEGynnsamHa;
                            const mfel_arsskadaAndel = inv.medelfelAntalRASEGynnsamHa * 1.96;
                            const mfel_arsskadaAndel_low = (inv.medelfelAntalRASEGynnsamHa ?? 0) > 0 ? arsskadaAndel - mfel_arsskadaAndel : null;
                            const mfel_arsskadaAndel_high = (inv.medelfelAntalRASEGynnsamHa ?? 0) > 0 ? arsskadaAndel + mfel_arsskadaAndel : null;

                            // 3års medel
                            const arsskadaAndel3InvMedel = inv.antalRASEGynnsamHa3InvAVG;
                            const mfel_arsskadaAndel3InvMedel = inv.medelfelAntalRASEGynnsamHa3InvMedel * 1.96;
                            const mfel_arsskadaAndel3InvMedel_low =
                                (inv.medelfelAntalRASEGynnsamHa3InvMedel ?? 0) > 0 ? arsskadaAndel3InvMedel - mfel_arsskadaAndel3InvMedel : null;
                            const mfel_arsskadaAndel3InvMedel_high =
                                (inv.medelfelAntalRASEGynnsamHa3InvMedel ?? 0) > 0 ? arsskadaAndel3InvMedel + mfel_arsskadaAndel3InvMedel : null;

                            data.push([
                                `${inv.invAr}`,
                                arsskadaAndel > 0 ? arsskadaAndel : null,
                                mfel_arsskadaAndel_low,
                                mfel_arsskadaAndel_high,
                                this.getTooltip2(false, arsskadaAndel, mfel_arsskadaAndel, 'Gynnsam 1 år', 'Gynnsam senaste 3 inv', '1.0-0', false),
                                arsskadaAndel3InvMedel,
                                mfel_arsskadaAndel3InvMedel_low,
                                mfel_arsskadaAndel3InvMedel_high,
                                this.getTooltip2(true, arsskadaAndel3InvMedel, mfel_arsskadaAndel3InvMedel, 'Gynnsam 1 år', 'Gynnsam senaste 3 inv',  '1.0-0', false)
                            ]);
                        }
                    });
                    this.options = {
                        ...this.options,
                        tooltip: { isHtml: true },
                        interval: {
                            Medelfel_low: { style: 'bars', lineWidth: 1 },
                            Medelfel_high: { style: 'bars', lineWidth: 1 },
                            Medelfel_3ar_low: { style: 'area', curveType: 'none', fillOpacity: 0.48 },
                            Medelfel_3ar_high: { style: 'area', curveType: 'none', fillOpacity: 0.48 }
                        },
                        height: 250,
                        vAxis: {
                            ...this.options.vAxis,
                            title: 'Antal',
                            maxValue: this.getMaxValue(data)
                        }
                    };

                    this.data = data;
                }
            })
        );

        this.subs.push(
            this.googleCombochart.chartImg$.subscribe((chartImgObj: { chartName: string; imgUri: string }) => {
                this.chartToImageSvc.setChartImage(chartImgObj);
            })
        );
    }

    ngOnDestroy(): void {
        this.subs.forEach((x) => x.unsubscribe());
    }
}
